import GenericTable from "@/components/GenericTable";
import OtpApprovalModal from "@/components/OtpApprovalModal";
import { useOpenTotpSetup } from "@/components/TotpSetupModal";
import Footer from "@/components/Footer";
import { toaster } from "@/components/ui/toaster";
import { ROUTES } from "@/config/routes";
import { PAYOUT_BATCHES_TABLE_COLUMNS } from "@/config/tables";
import { useFetchUser } from "@/hooks/useFetchUser";
import { useTableFiltersCursor } from "@/hooks/useTableFiltersCursor";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Badge,
  Box,
  Button,
  Dialog,
  HStack,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AlertTriangle, ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApprovePayoutBatch } from "../hooks/useApprovePayoutBatch";
import { useBuildingManagementEnabled } from "../hooks/useBuildingManagementEnabled";
import { useShowGmf } from "../hooks/useShowGmf";
import { useFetchPayoutBatches } from "../hooks/useFetchPayoutBatches";
import { useRejectPayoutBatch } from "../hooks/useRejectPayoutBatch";
import { mapPayoutBatchType } from "../mappers/payouts.mapper";
import type { BatchesFiltersType } from "../types/payouts.type";
import BatchesFilters from "./BatchesFilters";

const TABLE_KEY = "payouts-batches";

export default function BatchesTable() {
  const { queryFilters, setQueryFilters, cursor, setCursor } =
    useTableFiltersCursor<BatchesFiltersType>(TABLE_KEY);

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [selectedErrors, setSelectedErrors] = useState<
    { index: number; requestLine: string; errorMessage: string }[] | null
  >(null);
  const [otpModalBatchId, setOtpModalBatchId] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<string | null>(null);

  const { user } = useFetchUser();
  const openTotpSetup = useOpenTotpSetup();

  const { batches, nextCursor, isLoading } = useFetchPayoutBatches(
    queryFilters,
    cursor
  );
  const { approveBatch, isApproving } = useApprovePayoutBatch(
    (approvedBatch) => {
      setOtpModalBatchId(null);
      setOtpError(null);
      if (approvedBatch.status === "unfunded") {
        navigate(
          ROUTES.DASHBOARD.PAYOUTS_BATCH.replace(":batchId", approvedBatch.id),
          { state: { fund: true } }
        );
        return;
      }
      toaster.create({
        title: "Lote aprobado",
        description: "El lote de dispersión ha sido aprobado exitosamente.",
        type: "success",
        duration: 5000,
      });
    },
    (error) => {
      const message = error instanceof Error ? error.message : "";
      if (otpModalBatchId && message.includes("TOTP locked")) {
        setOtpError(
          "Clave Dinámica bloqueada temporalmente. Intenta más tarde."
        );
        return;
      }
      if (otpModalBatchId && message.includes("Invalid OTP")) {
        setOtpError("Clave Dinámica inválida. Intenta de nuevo.");
        return;
      }
      if (message.includes("Insufficient balance")) {
        toaster.create({
          title: "Saldo insuficiente",
          description: "No tienes saldo disponible para aprobar este lote.",
          type: "error",
          duration: 5000,
        });
      } else {
        toaster.create({
          title: "Error",
          description:
            "Ocurrió un error al aprobar el lote. Por favor, intenta de nuevo más tarde.",
          type: "error",
          duration: 5000,
        });
      }
    }
  );
  const { rejectBatch, isRejecting } = useRejectPayoutBatch(
    () => {
      toaster.create({
        title: "Lote rechazado",
        description: "El lote de dispersión ha sido rechazado exitosamente.",
        type: "success",
        duration: 5000,
      });
    },
    () => {
      toaster.create({
        title: "Error",
        description:
          "Ocurrió un error al rechazar el lote. Por favor, intenta de nuevo más tarde.",
        type: "error",
        duration: 5000,
      });
    }
  );
  const navigate = useNavigate();
  const { isPayoutApprover } = usePermissions();
  const isBuildingManagementEnabled = useBuildingManagementEnabled();
  const showGmf = useShowGmf();

  const columns = [
    { key: "id", label: "Lote ID" },
    ...(isBuildingManagementEnabled ? [{ key: "type", label: "Tipo" }] : []),
    { key: "createdAt", label: "Fecha de Creación" },
    { key: "status", label: "Estado" },
    { key: "totalCount", label: "Cant. Pagos" },
    { key: "totalAmount", label: "Monto Total" },
    { key: "totalFees", label: "Comisión" },
    ...(showGmf ? [{ key: "gmfTotal", label: "GMF" }] : []),
    { key: "discountedAmount", label: "Monto Descontado" },
    { key: "action", label: "" },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "medium",
    }).format(new Date(dateString));
  };

  const handleRowClick = (batchId: string) => {
    navigate(ROUTES.DASHBOARD.PAYOUTS_BATCH.replace(":batchId", batchId));
  };

  return (
    <Box display="flex" flexDirection="column" flex="1" minH={0}>
      <BatchesFilters
        queryFilters={queryFilters}
        setQueryFilters={setQueryFilters}
      />
      <GenericTable
        isLoading={isLoading}
        columns={columns}
        interactive
        fillHeight
      >
        {batches.map((batch) => (
          <Table.Row key={batch.id}>
            <Table.Cell
              cursor="pointer"
              onClick={() => handleRowClick(batch.id)}
            >
              <Text fontWeight="medium" color="palomma.600">
                {batch.id}
              </Text>
            </Table.Cell>
            {isBuildingManagementEnabled && (
              <Table.Cell
                cursor="pointer"
                onClick={() => handleRowClick(batch.id)}
              >
                <Badge
                  colorPalette={mapPayoutBatchType[batch.type ?? "owner"].color}
                  variant="subtle"
                >
                  {mapPayoutBatchType[batch.type ?? "owner"].label}
                </Badge>
              </Table.Cell>
            )}
            <Table.Cell
              cursor="pointer"
              onClick={() => handleRowClick(batch.id)}
              color="gray.600"
            >
              {formatDate(batch.createdAt)}
            </Table.Cell>
            <Table.Cell
              cursor="pointer"
              onClick={() => handleRowClick(batch.id)}
            >
              <Badge
                colorPalette={
                  batch.status === "approved"
                    ? "green"
                    : batch.status === "failed"
                    ? "red"
                    : batch.status === "cancelled"
                    ? "orange"
                    : batch.status === "preparing"
                    ? "blue"
                    : batch.status === "unfunded"
                    ? "yellow"
                    : batch.status === "funding"
                    ? "teal"
                    : "gray"
                }
                variant="subtle"
              >
                {batch.status === "approved"
                  ? "Aprobado"
                  : batch.status === "cancelled"
                  ? "Cancelado"
                  : batch.status === "failed"
                  ? "Fallido"
                  : batch.status === "preparing"
                  ? "Preparando"
                  : batch.status === "unfunded"
                  ? "Sin fondos"
                  : batch.status === "funding"
                  ? "Fondos en camino"
                  : "Pendiente"}
              </Badge>
            </Table.Cell>
            <Table.Cell
              cursor="pointer"
              onClick={() => handleRowClick(batch.id)}
              color="gray.600"
            >
              {batch.totalCount}
            </Table.Cell>
            <Table.Cell
              cursor="pointer"
              onClick={() => handleRowClick(batch.id)}
            >
              <Text fontWeight="bold" color="gray.800">
                {formatCurrency(batch.totalAmount)}
              </Text>
            </Table.Cell>
            <Table.Cell
              cursor="pointer"
              onClick={() => handleRowClick(batch.id)}
            >
              <Text fontWeight="medium" color="gray.600">
                {formatCurrency(batch.totalFees)}
              </Text>
            </Table.Cell>
            {showGmf && (
              <Table.Cell
                cursor="pointer"
                onClick={() => handleRowClick(batch.id)}
              >
                <Text fontWeight="medium" color="gray.600">
                  {formatCurrency(batch.gmfTotal ?? 0)}
                </Text>
              </Table.Cell>
            )}
            <Table.Cell
              cursor="pointer"
              onClick={() => handleRowClick(batch.id)}
            >
              <Text fontWeight="bold" color="gray.800">
                {formatCurrency(
                  batch.totalOutflows ??
                    batch.totalAmount + batch.totalFees + (batch.gmfTotal ?? 0)
                )}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <HStack gap={2} justifyContent="flex-end">
                {batch.errors && batch.errors.length > 0 && (
                  <Button
                    size="xs"
                    colorPalette="red"
                    variant="subtle"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedErrors(batch.errors || null);
                      setErrorModalOpen(true);
                    }}
                  >
                    <AlertTriangle size={16} /> Error
                  </Button>
                )}
                {isPayoutApprover && batch.status === "pending" && (
                  <>
                    <Button
                      size="xs"
                      colorPalette="red"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        rejectBatch(batch.id);
                      }}
                      loading={isRejecting}
                    >
                      <XCircle size={16} /> Rechazar
                    </Button>
                    <Button
                      size="xs"
                      colorPalette="palomma"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (user?.showTotpSecretConfiguration) {
                          openTotpSetup();
                          return;
                        }
                        setOtpModalBatchId(batch.id);
                        setOtpError(null);
                      }}
                      loading={isApproving}
                    >
                      <CheckCircle2 size={16} /> Aprobar
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleRowClick(batch.id)}
                >
                  <ArrowRight size={16} color="#64748B" />
                </Button>
              </HStack>
            </Table.Cell>
          </Table.Row>
        ))}
      </GenericTable>

      <Dialog.Root
        open={errorModalOpen}
        onOpenChange={(e) => setErrorModalOpen(e.open)}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>
                <HStack gap={2}>
                  <AlertTriangle color="#e53e3e" />
                  Error del Lote
                </HStack>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={4}>
                {selectedErrors?.map((error, idx) => (
                  <Box
                    key={idx}
                    p={3}
                    bg="red.50"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="red.200"
                  >
                    <Text
                      fontSize="xs"
                      color="red.500"
                      fontWeight="bold"
                      mb={1}
                    >
                      Fila {error.index + 1}: {error.requestLine}
                    </Text>
                    <Text fontSize="sm" color="red.700">
                      {error.errorMessage}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button onClick={() => setErrorModalOpen(false)}>Cerrar</Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>

      <Box mt="auto" pt={6} flexShrink={0}>
        <Footer
          tableProps={{
            fileName: "lotes_dispersion",
            items: batches,
            columns: PAYOUT_BATCHES_TABLE_COLUMNS.filter(
              (column) =>
                (isBuildingManagementEnabled || column.key !== "type") &&
                (showGmf || column.key !== "gmfTotal")
            ),
            pagination: {
              tableKey: TABLE_KEY,
              nextCursor,
              setCursor,
              cursor,
            },
          }}
        />
      </Box>

      <OtpApprovalModal
        isOpen={!!otpModalBatchId}
        onClose={() => {
          setOtpModalBatchId(null);
          setOtpError(null);
        }}
        onConfirm={(otp) => {
          if (otpModalBatchId) {
            setOtpError(null);
            approveBatch(otpModalBatchId, otp);
          }
        }}
        hasTotpSecret={user?.hasTotpSecret ?? false}
        isLoading={isApproving}
        error={otpError}
      />
    </Box>
  );
}
